import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { useTenantBranding } from '../contexts/TenantContext.jsx'
import { Button, Input, Alert } from '../components'
import { identityService } from '../shared/api'
import { useT } from '../shared/i18n/LanguageContext.jsx'
import LanguageToggle from '../shared/i18n/LanguageToggle.jsx'

/**
 * THE FIRST SCREEN TRANSLATED, and the only one where the language control HAS to be visible.
 *
 * Every other rung of the precedence chain is out of reach here. There is no account yet, so no
 * preference to consult; a settings screen is behind a login somebody cannot read their way into.
 * The device guess and the company default do the work for most people, but when they are wrong this
 * page is the only place left to fix it — which is why "English / Español" sits at the top, above the
 * form, where somebody who cannot read a word of this page can still find the word they recognise.
 */
const Login = () => {
  const t = useT('auth')
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState({})
  const [showAlert, setShowAlert] = useState(false)

  const { login, logout, loading, error, clearError } = useAuth()
  const branding = useTenantBranding()

  // Handle logout flag if present in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const isLogout = urlParams.get('logout') === 'true'
    
    if (isLogout) {
      // Clear all authentication data when logout flag is present
      console.log('Logout flag detected, clearing authentication...')
      logout()
      // Clear the logout flag from URL
      window.history.replaceState(null, '', '/login')
    }
  }, [logout])

  // Clear errors when component unmounts or form changes
  useEffect(() => {
    return () => clearError()
  }, [clearError])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('emailInvalid')
    }

    if (!formData.password) {
      newErrors.password = t('passwordRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return

    const result = await login(formData)
    
    if (result.success) {
      // Route by ROLE, not host: the platform superuser goes to the tenant-management
      // console; everyone else lands on the app portal. (Previously keyed on the admin.*
      // host, so a system_admin on tynvara.com wrongly fell through to /portal.)
      if (result.user?.role === 'system_admin') {
        console.log('System admin login — redirecting to Super Admin console')
        window.location.href = '/super-admin'
      } else {
        console.log('Login successful, redirecting to Portal')
        window.location.href = '/portal'
      }
    } else {
      setShowAlert(true)
    }
  }

  return (
    <div className="min-h-screen">
      {/* Construction Header Background */}
      <div className="bfs-construction-header py-12" style={{ backgroundColor: branding.primaryColor }}>
        {/* Two words, in the corner, ABOVE everything else on the page — the first thing a person who
            cannot read this screen can act on. No flags (a flag is a country; a Mexican hand and a
            Salvadoran hand share no flag but share this language) and no codes (`ES` is not a word to
            somebody who does not already know what it stands for). */}
        <div className="max-w-md mx-auto px-4 flex justify-end -mt-6 mb-2">
          <LanguageToggle className="text-sm text-white" />
        </div>
        <div className="max-w-md mx-auto text-center px-4">
          <img
            src={branding.logoUrl}
            alt={branding.name}
            className="h-32 w-auto max-w-xs mx-auto mb-6"
            style={{ objectFit: 'contain' }}
          />
          <h1 className="text-3xl font-bold bfs-heading-dark mb-2 text-white">
            {t('welcomeBack')}
          </h1>
          <p className="text-lg text-gray-100">
            {t('signInToAccess', branding.name)}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">

          {/* Login Form */}
          <div className="bfs-card p-8">
            {(error || showAlert) && (
              <Alert
                type="error"
                title={t('signInFailed')}
                // `error` is the SERVER's prose and stays in the language the server speaks — the
                // response carries no message code to switch on. It is the same honest limitation
                // the QR page documents at `SERVER_MESSAGES`, and the same sanctioned fallback:
                // English text a person can show their foreman, never a blank and never a code.
                message={error || t('checkEmailAndPassword')}
                onClose={() => {
                  setShowAlert(false)
                  clearError()
                }}
                className="mb-6"
              />
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label={t('emailAddress')}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                placeholder={t('emailPlaceholder')}
                required
                autoComplete="email"
                autoFocus
              />

              <Input
                label={t('password')}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
                placeholder={t('passwordPlaceholder')}
                required
                autoComplete="current-password"
              />

              <div className="flex items-center justify-between">
                <Link
                  to="/reset-password"
                  className="bfs-link text-sm"
                >
                  {t('forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={loading}
                className="w-full bfs-button-primary"
              >
                {loading ? t('signingIn') : t('signIn')}
              </Button>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-sm text-gray-600">
              {t('needHelp')}{' '}
              <span className="text-bfs-black font-semibold">{t('contactAdministrator')}</span>
            </p>
            <p className="text-xs text-gray-400 mt-3">
              Tynvara &middot; v{import.meta.env.VITE_APP_VERSION || '0.0.0-dev'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
